import { _ } from "~/i18n";
import ParamsItemSelectItemCar from "./params/item.select.item-car";
import ParamsItemSelectItem from "./params/item.select.item";

interface ILineStyle {
	width?: number;
	color?: string;
	opacity?: number;
	outlineWidth?: number;
	outlineColor?: string;
	outlineOpacity?: number;
	style?: number;
	endCap?: number;
	exactStyle?: string;
}

interface IActiveInactiveStyles {
	active: ILineStyle;
	deactive: ILineStyle;
}

export interface IRouteTypeSubitemItem {
	label: string;
	desc?: string;
	selected?: boolean;
	value: {
		criterion: number;
		useTraffic?: boolean;
	} | {
		criterion: number;
		allowViaFerrata: boolean;
	} | boolean | number;
	beta?: boolean;
	premium?: boolean;
}

interface IRouteTypeSubitem {
	type: string;
	name?: string;
	variable?: string;
	items?: Array<IRouteTypeSubitemItem>;
	itemFactory?: (item: IRouteTypeSubitemItem, userIsPremium: boolean) => any;
	constant?: number;
	label?: string;
	param?: string;
}

interface IRouteType {
	name: string;
	svg: string;
	type: number;
	def: number;
	criterion: Array<number>;
	subItems?: Array<IRouteTypeSubitem>;
}

export interface IRouteConst {
	TYPE_CAR: number;
	TYPE_PUBT: number;
	TYPE_BIKE: number;
	TYPE_WALK: number;
	TYPE_SKI: number;
	TYPE_BOAT: number;
	TYPE_CAR_FAST_CRITERIONS: [number, number];
	TYPE_CAR_AVOID_TOLL_CRITERIONS: [number, number];
	DEFAULT_LINE_STYLE: ILineStyle;
	CAR_LINE_STYLE_MODIFY: ILineStyle;
	CAR_TRAFFIC_LINE_STYLE_MODIFY: ILineStyle;
	PUBT_LINE_STYLE_MODIFY: ILineStyle;
	PUBT_WALK_LINE_STYLE_MODIFY: ILineStyle;
	PUBT_HOVER_LINE_STYLE_MODIFY: ILineStyle;
	ACTIVE_LINE_COLOR: ILineStyle;
	DEACTIVE_LINE_COLOR: ILineStyle;
	CLOSURE_LINE_COLOR: ILineStyle;
	TRAFFIC_LINE_STYLE: ILineStyle;
	TRAFFIC_LINE_COLOR: Array<IActiveInactiveStyles>;
	DEFAULT_CIRCLE_RADIUS: number;
	DEFAULT_CIRCLE_STYLE: ILineStyle;
	DEACTIVE_CIRCLE_STYLE: ILineStyle;
	CLOSURE_CIRCLE_STYLE: ILineStyle;
	types: {
		car: IRouteType;
		pubt: IRouteType;
		bike: IRouteType;
		trial: IRouteType;
		ski: IRouteType;
		water: IRouteType;
	};
	circuitTypes: {
		bike: IRouteType;
		trial: IRouteType;
		ski: IRouteType;
	};
	position: {
		start: number;
		middle: number;
		end: number;
	};
	itinerary: any;
	closureImages: {
		[key: number]: string;
	}
}

const TYPE_CAR = 1;
const TYPE_PUBT = 2;
const TYPE_BIKE = 3;
const TYPE_WALK = 4;
const TYPE_SKI = 5;
const TYPE_BOAT = 6;

export const ROUTE_CONST: IRouteConst = {
	TYPE_CAR,
	TYPE_CAR_FAST_CRITERIONS: [111, 112], // spravne kriteria pro rychlou trasu autem
	TYPE_CAR_AVOID_TOLL_CRITERIONS: [112, 114],
	TYPE_PUBT,
	TYPE_BIKE,
	TYPE_WALK,
	TYPE_SKI,
	TYPE_BOAT,
	DEFAULT_LINE_STYLE: {
		width: 6,
		color: "#0033ff",
		opacity: 0.6,
		outlineWidth: 2,
		outlineColor: "white",
		outlineOpacity: 0.4,
		style: JAK.Vector.STYLE_DASH,
		endCap: JAK.Vector.END_STYLE_BUTT,
		exactStyle: "14px 10px",
	},
	CAR_LINE_STYLE_MODIFY: {
		style: JAK.Vector.STYLE_SOLID,
		endCap: JAK.Vector.END_STYLE_SQUARE,
		exactStyle: undefined,
	},
	CAR_TRAFFIC_LINE_STYLE_MODIFY: {
		width: 8,
	},
	PUBT_LINE_STYLE_MODIFY: {
		outlineWidth: 3,
		opacity: 0.8,
		outlineOpacity: 0.5,
		style: JAK.Vector.STYLE_SOLID,
		endCap: JAK.Vector.END_STYLE_SQUARE,
		exactStyle: undefined,
		color: "#1263b5",
	},
	PUBT_WALK_LINE_STYLE_MODIFY: {
		outlineWidth: 3,
		opacity: 0.8,
		outlineOpacity: 0.5,
		color: "#362f28",
	},
	PUBT_HOVER_LINE_STYLE_MODIFY: {
		color: "#cc0000",
		opacity: 1,
	},
	ACTIVE_LINE_COLOR: {
		color: "#0033ff",
	},
	DEACTIVE_LINE_COLOR: {
		color: "#454C54",
	},
	CLOSURE_LINE_COLOR: {
		color: "#ff0000",
	},
	TRAFFIC_LINE_STYLE: {
		width: 4,
		opacity: 1,
	},
	TRAFFIC_LINE_COLOR: [{
		// 0 je stejna barva jako stupen 1 - ale to je jedno, ty s 0 vubec nevykreslujeme
		active: {
			color: "#00d654", // barva pro aktivni geometrii s trafficem
		},
		deactive: {
			color: "#00d654", // barva pro neaktivni/alternativni geometrii s trafficem
		},
	}, {
		// dle zadani nezobrazujeme ani prvni stupen, ale to se ridi az v kodu
		active: {
			color: "#00d654",
		},
		deactive: {
			color: "#00d654",
		},
	}, {
		active: {
			color: "#ffb000",
		},
		deactive: {
			color: "#b3934d",
		},
	}, {
		active: {
			color: "#ff1300",
		},
		deactive: {
			color: "#8f433d",
		},
	}, {
		active: {
			color: "#960000",
		},
		deactive: {
			color: "#592626",
		},
	}],
	DEFAULT_CIRCLE_RADIUS: 4,
	DEFAULT_CIRCLE_STYLE: {
		color: "#fff",
		opacity: 1,
		outlineWidth: 1,
		outlineColor: "#0033ff",
	},
	DEACTIVE_CIRCLE_STYLE: {
		outlineColor: "#454C54",
	},
	CLOSURE_CIRCLE_STYLE: {
		outlineColor: "#ff0000",
	},
	types: {
		car: {
			name: _("route.params.typeCar"),
			svg: "car",
			type: TYPE_CAR,
			def: 111,
			criterion: [111, 112, 113, 114],
			subItems: [
				{
					type: "select",
					name: "route-car-type",
					variable: "criterion",
					items: [{
						label: _("route.params.fastTraffic"),
						desc: _("route.params.fastTrafficDesc"),
						selected: true,
						value: {
							criterion: 111,
							useTraffic: true,
						},
					}, {
						label: _("route.params.fast"),
						desc: _("route.params.fastDesc"),
						value: {
							criterion: 111,
						},
					}, {
						label: _("route.params.short"),
						desc: _("route.params.shortDesc"),
						value: {
							criterion: 113,
						},
					}],
					itemFactory: item => new ParamsItemSelectItemCar(item),
				},
			],
		},
		pubt: {
			name: _("route.params.typePubt"),
			svg: "pubt",
			type: TYPE_PUBT,
			def: 200,
			criterion: [200],
			subItems: [
				{
					type: "pubt",
					name: "route-pubt",
					param: "pubt",
				},
			],
		},
		trial: {
			name: _("route.params.typeTrial"),
			svg: "walk",
			type: TYPE_WALK,
			def: 132,
			criterion: [131, 132],
			subItems: [
				{
					type: "select",
					name: "route-trial-type",
					variable: "criterion",
					items: [{
						label: _("route.params.fast"),
						value: 131,
					}, {
						label: _("route.params.turist"),
						selected: true,
						value: 132,
					}],
					itemFactory: (item, userIsPremium) => new ParamsItemSelectItem(item, userIsPremium),
				},
			],
		},
		bike: {
			name: _("route.params.typeBike"),
			svg: "bike",
			type: TYPE_BIKE,
			def: 121,
			criterion: [121, 122],
			subItems: [
				{
					type: "select",
					name: "route-bike-type",
					variable: "criterion",
					items: [{
						label: _("route.params.criterionBike1"),
						selected: true,
						value: 121,
					}, {
						label: _("route.params.criterionBike2"),
						value: 122,
					}],
					itemFactory: (item, userIsPremium) => new ParamsItemSelectItem(item, userIsPremium),
				},
			],
		},
		ski: {
			name: _("route.params.typeSki"),
			svg: "ski-cross-country",
			type: TYPE_SKI,
			def: 141,
			criterion: [141],
		},
		water: {
			name: _("route.params.typeWater"),
			svg: "boat",
			type: TYPE_BOAT,
			def: 144,
			criterion: [143, 144],
		},
	},
	circuitTypes: {
		trial: {
			name: _("route.params.typeTrial"),
			svg: "walk",
			type: TYPE_WALK,
			def: 132,
			criterion: [132],
		},
		bike: {
			name: _("route.params.typeBike"),
			svg: "bike",
			type: TYPE_BIKE,
			def: 121,
			criterion: [121],
		},
		ski: {
			name: _("route.params.typeSki"),
			svg: "ski-cross-country",
			type: TYPE_SKI,
			def: 141,
			criterion: [141],
		},
	},
	position: {
		start: 0,
		middle: 1,
		end: 2,
	},
	itinerary: {
		turistTypes: {
			Red: {
				icon: "pas-cer",
				width: 17,
				height: 15,
			},
			Blue: {
				icon: "pas-mod",
				width: 17,
				height: 15,
			},
			Green: {
				icon: "pas-zel",
				width: 17,
				height: 15,
			},
			Yellow: {
				icon: "pas-zlu",
				width: 17,
				height: 15,
			},
			Black: {
				icon: "pas-cerna",
				width: 17,
				height: 15,
			},
			Purple: {
				icon: "pas-fialova",
				width: 17,
				height: 15,
			},
			Brown: {
				icon: "pas-hneda",
				width: 17,
				height: 15,
			},
			Orange: {
				icon: "pas-oranzova",
				width: 17,
				height: 15,
			},
			LocalRed: {
				icon: "mis-cer",
				width: 15,
				height: 15,
			},
			LocalBlue: {
				icon: "mis-mod",
				width: 15,
				height: 15,
			},
			LocalGreen: {
				icon: "mis-zel",
				width: 15,
				height: 15,
			},
			LocalYellow: {
				icon: "mis-zlu",
				width: 15,
				height: 15,
			},
			EducationTrail: {
				icon: "stezka",
				width: 15,
				height: 15,
			},
		},
		turistImages: {
			turnstraight: "nahoru",
			turnslightlyleft: "mir-leva",
			turnleft: "leva",
			turnsharpleft: "leva",
			turnslightlyright: "mir-prava",
			turnright: "prava",
			turnsharpright: "prava",
			turnback: "dolu",
		},
		carImages: {
			turnstraight: "rovne",
			turnslightlyleft: "mirne_vlevo",
			turnleft: "vlevo",
			turnsharpleft: "ostre_vlevo",
			turnslightlyright: "mirne_vpravo",
			turnright: "vpravo",
			turnsharpright: "ostre_vpravo",
			turnback: "zpet",
			keepleft: "mirne_vlevo",
			keepright: "mirne_vpravo",
			keepinmiddle: "rovne",
			roundabout: "kruhac",
			highwayexit: "sjezd",
			exit: "exit",
		},
	},
	closureImages: {
		[TYPE_CAR]: "no-entry-car",
		[TYPE_BIKE]: "no-entry-bike",
		[TYPE_WALK]: "no-entry-walk",
	},
};
